import React, { useState } from 'react';
import {
  Badge, Button, Form, Table,
} from 'react-bootstrap';
import {
  AGENT_MODES,
  EXECUTING_AUTHORITY,
  authorityDescriptor,
  authorityOf,
  effectiveAuthority,
  executableTools,
  grantedTools,
  isAuthorityAllowed,
  modeDescriptor,
  reconcileGuidanceTools,
} from '../../../Services/AiInstructionsService';

/**
 * WHAT THIS AGENT MAY CALL — the mode, and one authority per tool.
 *
 * ── THE CATALOGUE IS THE SERVER'S, ALWAYS ──
 * `tools` and `authorities` arrive on the scopes payload and this component
 * renders what it is handed. There is no tool list in the dashboard and there
 * must not be: a tool's name is the name of a Python `@tool` function, the one
 * mirror of those lives on the server beside the gate that checks it, and a
 * second copy here would be a list of names nobody validates. A tool added in
 * Python and declared on the server appears here with no dashboard change; one
 * removed disappears.
 *
 * ── THE CEILING IS SHOWN, NOT DISCOVERED BY BEING REFUSED ──
 * Every tool carries a `maxAuthority` that no configuration may exceed, set in
 * code and raised only by a deploy. An authority above it is rendered as a
 * DISABLED option with the reason in the row, rather than as a choice that
 * turns into a 400 after the save. The server still enforces it — this is what
 * the screen shows, not what decides.
 *
 * ── THE MODE SITS ABOVE THE MAP, AND OVERRIDES IT ──
 * In `shadow` every tool that writes comes down to `propose`, whatever its own
 * setting says, so a per-tool value is NOT on its own what the agent would do.
 * A screen that showed forty-five authorities under a mode that overrides them
 * would be lying in the most expensive possible place, so a row whose
 * effective authority differs from its configured one says so on the row.
 *
 * READS STILL EXECUTE IN `shadow`, and the copy says that rather than the
 * easier, wronger "nothing runs": an agent that cannot look up the thread, the
 * booking or the price does not propose a reply, it invents one.
 *
 * ── IT OWNS NO SAVE ──
 * The mode and the map are part of the DRAFT — one row on the server, frozen
 * by one publish — so the draft panel's single Save is what keeps them. A save
 * button here would be a second way to write the same row and a second thing
 * to get wrong.
 */

/**
 * The catalogue grouped by its Python module, in catalogue order.
 *
 * The order the server sends is the order the file is authored in, which is
 * already grouped and already sorted by how closely a module sits to the
 * office's core records. Re-sorting alphabetically would throw that away for
 * nothing.
 */
export function byModule(tools) {
  const groups = [];

  (Array.isArray(tools) ? tools : []).forEach((tool) => {
    const existing = groups.find((group) => group.module === tool.module);
    if (existing) existing.tools.push(tool);
    else groups.push({ module: tool.module, tools: [tool] });
  });

  return groups;
}

/** The top of the served ranking — what a ceiling is measured against. */
const topAuthority = (authorities) => (
  Array.isArray(authorities) && authorities.length
    ? authorities[authorities.length - 1]
    : undefined
);

/** An authority as a word, falling back to its own value. */
const authorityLabel = (value) => authorityDescriptor(value)?.label || value || '—';

/** A mode as a word, falling back to its own value. */
const modeLabel = (value) => modeDescriptor(value)?.label || value || '—';

/**
 * WHERE THE GUIDANCE AND THE MAP DISAGREE — said here, not by the server.
 *
 * The owner was asked to write guidance of the form "when this happens, call
 * this tool for results, then create a price". Written against a scope with
 * nothing granted, the save is refused once per tool name: sixteen opaque
 * errors for a body that says exactly what it was asked to say. The RULE is
 * right — prose that names a tool the agent does not hold is a sentence
 * nobody will ever execute. Meeting it as a server refusal is the bug.
 *
 * Every row here carries the fix as a button, so the cross-check is satisfied
 * before Save rather than discovered by it. Advisory: the server still
 * decides, and this only ever offers to make the two agree.
 */
function GuidanceToolCheck({
  report, tools, authorities, onChange, disabled,
}) {
  const {
    ungranted, unknown, proposeOnly, unmentioned,
  } = report;

  if (!ungranted.length && !unknown.length && !proposeOnly.length && !unmentioned.length) {
    return null;
  }

  const labelOf = (key) => tools.find((t) => t.key === key)?.label || key;

  /**
   * The most a configuration may give this tool: `auto` where the ceiling
   * allows it, the ceiling otherwise. "Grant" has to mean "can actually call
   * it", and for a booking write the honest most is `propose` — which the
   * `proposeOnly` list below then reports, rather than this quietly pretending.
   */
  const bestGrant = (key) => {
    const tool = tools.find((t) => t.key === key);
    if (!tool) return null;
    return isAuthorityAllowed(authorities, tool, EXECUTING_AUTHORITY)
      ? EXECUTING_AUTHORITY
      : tool.maxAuthority;
  };

  return (
    <div className="ai-tools-check">
      {ungranted.length > 0 && (
        <div className="ai-panel ai-panel--warn ai-tools-check-block">
          <div className="ai-panel-title">
            <i className="fa fa-link-slash me-2" aria-hidden="true" />
            {`Your guidance names ${ungranted.length} tool${ungranted.length === 1 ? '' : 's'} this agent does not hold`}
          </div>
          <p>
            {'Saving will be refused until the two agree — the server checks'
             + ' every tool the prose names against what the scope holds. Grant'
             + ' it here, or take the name out of the guidance.'}
          </p>
          <ul className="ai-tools-check-list">
            {ungranted.map((key) => (
              <li key={key}>
                <code className="ai-tool-key">{key}</code>
                <span className="ai-tools-check-label">{labelOf(key)}</span>
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={disabled}
                  onClick={() => onChange(key, bestGrant(key))}
                >
                  {`Grant it — ${authorityLabel(bestGrant(key))}`}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {unknown.length > 0 && (
        <div className="ai-panel ai-panel--warn ai-tools-check-block">
          <div className="ai-panel-title">
            <i className="fa fa-circle-question me-2" aria-hidden="true" />
            {`${unknown.length} name${unknown.length === 1 ? ' is' : 's are'} not a tool at all`}
          </div>
          <p className="mb-0">
            {'Written as '}
            <code>tool:</code>
            {' but not in the catalogue, so there is nothing to grant — check'
             + ' the spelling, or it may have been renamed or removed: '}
            {unknown.map((name) => <code key={name} className="ai-tool-key">{name}</code>)}
          </p>
        </div>
      )}

      {proposeOnly.length > 0 && (
        <div className="ai-panel ai-panel--warn ai-tools-check-block">
          <div className="ai-panel-title">
            <i className="fa fa-user-clock me-2" aria-hidden="true" />
            {`${proposeOnly.length} named tool${proposeOnly.length === 1 ? '' : 's'} will NOT execute`}
          </div>
          <p className="mb-0">
            {'Granted, so this saves and publishes cleanly — and then the call'
             + ' never runs. Only '}
            <strong>{authorityLabel(EXECUTING_AUTHORITY)}</strong>
            {' is handed to the model; a '}
            <strong>{authorityLabel('propose')}</strong>
            {' is captured for a person to approve, and there is no approval'
             + ' step built yet. Guidance that says "call this" is not true of: '}
            {proposeOnly.map((name) => <code key={name} className="ai-tool-key">{name}</code>)}
          </p>
        </div>
      )}

      {unmentioned.length > 0 && (
        <p className="ai-panel-note ai-tools-check-quiet">
          {`Granted but never named in the guidance (${unmentioned.length}): `}
          {unmentioned.map((name) => <code key={name} className="ai-tool-key">{name}</code>)}
          {'. Not a fault — the model reads each tool\'s own description, so'
           + ' good guidance rarely names one. Listed in case a rule is missing.'}
        </p>
      )}
    </div>
  );
}

function AgentToolMap({
  tools = [],
  authorities = [],
  value = {},
  mode = 'shadow',
  publishedMode,
  publishedValue,
  body = '',
  onChange,
  onModeChange,
  onInsertTool,
  disabled = false,
}) {
  const [grantedOnly, setGrantedOnly] = useState(false);

  const granted = grantedTools(value);
  const groups = byModule(tools);
  const ceiling = topAuthority(authorities);
  const shadow = mode !== 'live';

  const report = reconcileGuidanceTools({
    body, tools, toolAuthority: value, mode,
  });

  /**
   * WOULD THIS CONFIGURATION RUN AT ALL?
   *
   * The autonomous runtime hands the model only the tools on `auto` and
   * REFUSES THE WHOLE RUN when that set is empty — not "runs without tools":
   * "No tool in this scope's configuration is set to `auto`, so the agent has
   * nothing it may call." A drafter that cannot read the thread or the booking
   * does not decline to answer, it invents one, so refusing is correct.
   *
   * It is also invisible from the per-tool map, because a map full of
   * `propose` looks configured. Said here instead.
   */
  const executing = executableTools({ tools, toolAuthority: value, mode });
  const nothingExecutes = tools.length > 0 && executing.length === 0;

  /** The rows to draw: all of them, or only what the agent actually holds. */
  const visible = (group) => (
    grantedOnly
      ? group.tools.filter((tool) => granted.includes(tool.key))
      : group.tools
  );

  // A table of headers and no rows reads as a failed load. The filter hiding
  // everything is a legitimate state — a scope that holds no tools yet is the
  // DEFAULT state — so it has to say so in words.
  const shown = groups.reduce((count, group) => count + visible(group).length, 0);

  return (
    <section className="ai-panel ai-panel--flush ai-tools">
      <div className="ai-panel-title ai-panel-title--bar">
        What this agent may call
        <span className="ai-tools-count">
          {`${granted.length} of ${tools.length} granted · ${executing.length} would execute`}
        </span>
      </div>

      {/* The configuration that saves, publishes, and then does nothing. It
          cannot be seen in the map below, because a map full of `propose`
          looks configured. */}
      {nothingExecutes && (
        <div className="ai-panel ai-panel--warn ai-tools-check-block">
          <div className="ai-panel-title">
            <i className="fa fa-ban me-2" aria-hidden="true" />
            This agent would be refused at runtime
          </div>
          <p className="mb-0">
            {'Nothing is set to '}
            <strong>{authorityLabel(EXECUTING_AUTHORITY)}</strong>
            {', and only that is handed to the model. The run does not go ahead'
             + ' without tools — it is refused outright, because a drafter that'
             + ' cannot read the thread or the booking does not decline to'
             + ' answer, it invents one. Grant at least the reads it needs.'}
          </p>
        </div>
      )}

      <GuidanceToolCheck
        report={report}
        tools={tools}
        authorities={authorities}
        onChange={onChange}
        disabled={disabled}
      />

      <div className="ai-tools-mode">
        <span className="ai-panel-label">Mode</span>
        <div role="radiogroup" aria-label="Agent mode" className="ai-mode-options">
          {AGENT_MODES.map((option) => (
            <Form.Check
              key={option.value}
              type="radio"
              id={`agent-mode-${option.value}`}
              name="agent-mode"
              className="ai-mode-option"
              checked={mode === option.value}
              disabled={disabled}
              onChange={() => onModeChange(option.value)}
              label={(
                <>
                  <strong>{option.label}</strong>
                  <span className="ai-mode-detail">{option.detail}</span>
                </>
              )}
            />
          ))}
        </div>
        {publishedMode && publishedMode !== mode && (
          <p className="ai-panel-note mb-0">
            {`Live now: ${modeLabel(publishedMode)}. This draft changes it.`}
          </p>
        )}
      </div>

      {/* Not dismissible and not collapsed. An admin reading a map of
          authorities has to know the mode above it is overriding them. */}
      {shadow && (
        <div className="ai-panel ai-panel--warn ai-tools-shadow">
          <div className="ai-panel-title">
            <i className="fa fa-eye me-2" aria-hidden="true" />
            This agent is in Shadow
          </div>
          <p className="mb-0">
            {'No tool that changes a record executes, whatever its setting'
             + ' below says — the call is captured for a person to approve'
             + ' instead. Reads still run, which is the point of shadowing'
             + ' rather than switching the agent off: an agent that cannot look'
             + ' up the thread, the booking or the price does not propose a'
             + ' reply, it invents one.'}
          </p>
        </div>
      )}

      <div className="ai-tools-filter">
        <Form.Check
          type="switch"
          id="ai-tools-granted-only"
          label="Show only the tools this agent holds"
          checked={grantedOnly}
          onChange={(e) => setGrantedOnly(e.target.checked)}
        />
      </div>

      <Table className="ai-tool-table" aria-label="Tool authorities">
        <thead>
          <tr>
            <th>Tool</th>
            <th>Authority</th>
            <th>What that means here</th>
          </tr>
        </thead>
        <tbody>
          {tools.length === 0 && (
            <tr>
              <td colSpan={3} className="text-center text-muted py-4">
                The server sent no tool catalogue, so there is nothing to
                configure. The list comes from
                {' '}
                <code>/ai/instructions/scopes</code>
                .
              </td>
            </tr>
          )}

          {tools.length > 0 && shown === 0 && (
            <tr>
              <td colSpan={3} className="text-center text-muted py-4">
                This agent holds no tools at all, so there is nothing to show.
                Untick the filter to grant it some.
              </td>
            </tr>
          )}

          {groups.map((group) => {
            const rows = visible(group);
            if (rows.length === 0) return null;

            return (
              <React.Fragment key={group.module}>
                <tr className="ai-tool-module">
                  {/* The Python module under `chat-agent/tools/`, verbatim —
                      it is a filename, not a label, so it is not prettified. */}
                  <th colSpan={3} scope="colgroup">
                    <code>{group.module}</code>
                  </th>
                </tr>

                {rows.map((tool) => {
                  const configured = authorityOf(value, tool.key);
                  const effective = effectiveAuthority({
                    mode, tool, authority: configured,
                  });
                  const live = publishedValue
                    ? authorityOf(publishedValue, tool.key)
                    : null;
                  const capped = ceiling !== undefined
                    && tool.maxAuthority !== ceiling;

                  return (
                    <tr key={tool.key}>
                      <td data-label="Tool">
                        <span className="ai-tool-name">{tool.label}</span>
                        <code className="ai-tool-key">{tool.key}</code>
                        {tool.writes && (
                          <Badge bg="warning" text="dark" className="ai-tool-writes">
                            Writes
                          </Badge>
                        )}
                        {/* INSERT AND GRANT ARE ONE ACTION, deliberately.
                            Naming a tool in the prose and granting it are the
                            two halves of one intention, and the server refuses
                            the save when they disagree — so doing them in one
                            click satisfies the cross-check by construction
                            instead of leaving it to be discovered on Save. */}
                        {onInsertTool && (
                          <Button
                            size="sm"
                            variant="link"
                            className="ai-tool-insert"
                            disabled={disabled}
                            onClick={() => onInsertTool(tool.key)}
                          >
                            Name it in the guidance, and grant it
                          </Button>
                        )}
                      </td>

                      <td data-label="Authority">
                        <div
                          role="radiogroup"
                          aria-label={`Authority for ${tool.label}`}
                          className="ai-tool-authorities"
                        >
                          {authorities.map((option) => {
                            const allowed = isAuthorityAllowed(authorities, tool, option);
                            const descriptor = authorityDescriptor(option);
                            return (
                              <Form.Check
                                inline
                                key={option}
                                type="radio"
                                id={`authority-${tool.key}-${option}`}
                                name={`authority-${tool.key}`}
                                label={descriptor?.label || option}
                                checked={configured === option}
                                disabled={disabled || !allowed}
                                onChange={() => onChange(tool.key, option)}
                                title={allowed
                                  ? descriptor?.detail
                                  : `Capped at ${authorityLabel(tool.maxAuthority)} in code.`}
                              />
                            );
                          })}
                        </div>
                      </td>

                      <td data-label="What that means here">
                        {capped && (
                          <span className="ai-tool-note ai-tool-note--cap">
                            {`Capped at ${authorityLabel(tool.maxAuthority)} in code`}
                            {ceiling && ` — ${authorityLabel(ceiling)} is not available for this tool.`}
                            {' Raising it is a deploy, not a setting.'}
                          </span>
                        )}
                        {effective !== configured && (
                          <span className="ai-tool-note ai-tool-note--effective">
                            {`${authorityLabel(configured)} → held as`
                             + ` ${authorityLabel(effective)} while this agent is in Shadow.`}
                          </span>
                        )}
                        {live !== null && live !== configured && (
                          <span className="ai-tool-note ai-tool-note--live">
                            {`Live now: ${authorityLabel(live)}. This draft changes it.`}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>

      <p className="ai-panel-note ai-tools-foot">
        {'The mode and these authorities are part of the DRAFT: they are saved'
         + ' and published with the guidance above, under the same version, so'
         + ' what the agent was allowed to do when it ran is the same lookup as'
         + ' what it was told. Save the draft to keep a change here.'}
      </p>
    </section>
  );
}

export default AgentToolMap;
